import { useMemo, useState } from "react";
import { Button, Combobox, SectionCard } from "@a2z/ui";
import { DrawerSheet } from "@/shared/components/dialogs/DrawerSheet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CategoryView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import type { ProductsSort } from "./ProductsHeroBar";

type Props = {
  open: boolean;
  onClose: () => void;
  basePath: string;
  limit: number;
  locale: Locale;
  categories: CategoryView[];
  selectedCategoryIds: string[];
  sort: ProductsSort;
  onSortChange: (sort: ProductsSort) => void;
};

const SORT_OPTIONS: { value: ProductsSort; labelKey: string }[] = [
  { value: "recent", labelKey: keys.products.sortRecentlyAdded },
  { value: "sales", labelKey: keys.products.sortBestSelling },
  { value: "price-desc", labelKey: keys.products.sortPriceHighToLow },
  { value: "price-asc", labelKey: keys.products.sortPriceLowToHigh },
  { value: "stock-asc", labelKey: keys.products.sortStockLowToHigh },
  { value: "stock-desc", labelKey: keys.products.sortStockHighToLow },
];

/**
 * Mobile filters drawer for the products list. Surfaces sort and category
 * options that the dark hero exposed inline on desktop. Reuses the canonical
 * `<Drawer>` so it shares chrome with every other drawer in the app.
 */
export function ProductsFiltersDrawer({
  open,
  onClose,
  basePath,
  limit,
  locale,
  categories,
  selectedCategoryIds,
  sort,
  onSortChange,
}: Props) {
  const t = useTranslations();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Categories are staged locally so several can be picked before applying —
  // each toggle would otherwise navigate/refetch. Re-seed whenever the drawer
  // re-opens against a different URL selection.
  const [staged, setStaged] = useState<string[]>(selectedCategoryIds);
  const seedKey = open ? `open:${selectedCategoryIds.join(",")}` : "closed";
  const [prevSeedKey, setPrevSeedKey] = useState(seedKey);
  if (seedKey !== prevSeedKey) {
    // Render-time previous-value comparison instead of a setState-in-effect.
    setPrevSeedKey(seedKey);
    if (open) setStaged(selectedCategoryIds);
  }

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        value: c.id,
        label: pickLocalized(locale, c.nameAr, c.nameEn),
      })),
    [categories, locale],
  );

  const labelFor = (id: string) =>
    categoryOptions.find((o) => o.value === id)?.label ?? id;

  // Apply staged categories while preserving every other active filter
  // (availability / status / search) — only the category set + offset change.
  const applyCategories = (ids: string[]) => {
    const params = new URLSearchParams(searchParams);
    params.delete("categoryIds[]");
    ids.forEach((id) => params.append("categoryIds[]", id));
    params.set("limit", String(limit));
    params.set("offset", "0");
    navigate(`${basePath}?${params.toString()}`);
  };

  const handleApply = () => {
    applyCategories(staged);
    onClose();
  };

  const reset = () => {
    onSortChange("recent");
    setStaged([]);
    applyCategories([]);
  };

  return (
    <DrawerSheet
      isOpen={open}
      onClose={onClose}
      size="sm"
      icon={<FilterIcon />}
      title={t(keys.products.filtersAction)}
      subtitle={t(keys.products.filtersSubtitle)}
      closeLabel={t(keys.products.filtersCloseLabel)}
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={reset} className="flex-1">
            {t(keys.common.reset)}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleApply}
            className="flex-[2]"
          >
            {t(keys.common.applyFilters)}
          </Button>
        </>
      }
    >
      <SectionCard title={t(keys.products.category)}>
        <div className="flex flex-col gap-2.5">
          {/* Searchable multi-select — scales to long category lists; picks
              accumulate as removable chips below and commit on "Apply". */}
          <Combobox
            multiple
            options={categoryOptions}
            value={staged}
            onChange={setStaged}
            aria-label={t(keys.products.filterCategories)}
            placeholder={t(keys.products.filterCategoriesPlaceholder)}
            noResultsLabel={t(keys.search.noResults)}
            selectedCountLabel={t(keys.products.categoriesSelectedCount, {
              count: "{count}",
            })}
          />
          {staged.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {staged.map((id) => (
                <span
                  key={id}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[color-mix(in_oklab,var(--accent-500)_25%,var(--card))] bg-[color-mix(in_oklab,var(--accent-500)_8%,var(--card))] py-1 ps-2.5 pe-1.5 text-[13px] font-medium text-[color:var(--accent-600)]"
                >
                  <span className="truncate">{labelFor(id)}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setStaged(staged.filter((x) => x !== id))
                    }
                    aria-label={t(keys.products.clearFilter, {
                      filter: labelFor(id),
                    })}
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[color:var(--accent-600)] transition-colors hover:bg-[color-mix(in_oklab,var(--accent-500)_18%,var(--card))]"
                  >
                    <XIcon />
                  </button>
                </span>
              ))}
              {staged.length > 1 ? (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setStaged([])}
                  className="px-1.5"
                >
                  {t(keys.products.categoryFilterClearAll)}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title={t(keys.products.sortBy)}>
        <div className="flex flex-col gap-1">
          {SORT_OPTIONS.map((o) => (
            <RadioRow
              key={o.value}
              label={t(o.labelKey)}
              active={sort === o.value}
              onClick={() => onSortChange(o.value)}
            />
          ))}
        </div>
      </SectionCard>
    </DrawerSheet>
  );
}

function FilterIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      aria-hidden="true"
      width="9"
      height="9"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M1 1L9 9M9 1L1 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RadioRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-start text-[13px] transition-colors",
        active
          ? "border border-[color-mix(in_oklab,var(--accent-500)_25%,var(--card))] bg-[color-mix(in_oklab,var(--accent-500)_8%,var(--card))] font-semibold text-[color:var(--accent-600)]"
          : "border border-transparent font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--background-muted)]",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className="inline-block h-4 w-4 shrink-0 rounded-full border-[1.5px]"
        style={{
          borderColor: active ? "var(--accent-500)" : "var(--border-strong)",
          background: active ? "var(--accent-500)" : "transparent",
          boxShadow: active ? "inset 0 0 0 3px white" : "none",
        }}
      />
      {label}
    </button>
  );
}
